import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Spinner, Form, Toast, ToastContainer, Badge, InputGroup } from 'react-bootstrap';
import SEO from '../components/common/SEO';
import SkeletonLoader, { CartItemSkeleton } from '../components/common/SkeletonLoader';
import { apiService } from '../services/api';
import { Cart, Addon } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useConfirmation } from '../contexts/ConfirmationContext';
import AddonCard from '../components/cart/AddonCard';
import OmiseDrawer from '../components/cart/OmiseDrawer';
import CartError from '../components/cart/CartError';
import EmptyCart from '../components/cart/EmptyCart';
import './CartPage.scss';

const CartPage: React.FC = () => {
  const [cart, setCart] = useState<Cart | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorMessages, setErrorMessages] = useState<string[] | null>(null);
  const [removingItem, setRemovingItem] = useState<number | null>(null);
  const [couponCode, setCouponCode] = useState('');
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('');
  const [isPartialPayment, setIsPartialPayment] = useState(false);
  const [createAccount, setCreateAccount] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [cartTimer, setCartTimer] = useState<string>('00:00');
  const [shippingAddresses, setShippingAddresses] = useState<any[]>([]);
  const [selectedAddress, setSelectedAddress] = useState<number | null>(null);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<string | null>(null);
  const [omisePayment, setOmisePayment] = useState<{ qrCodeData: string; chargeId: string } | null>(null);
  const [omiseStatusMessage, setOmiseStatusMessage] = useState<string>('Waiting for payment...');
  const [omiseStatus, setOmiseStatus] = useState<'pending' | 'successful' | 'failed' | 'expired'>('pending');
  const [omiseConfig, setOmiseConfig] = useState<{
    sourceTypes: Array<[string, string]>;
    publicKey: string;
    amountSubunit: number;
    currency: string;
  } | null>(null);
  const [showOmiseSelector, setShowOmiseSelector] = useState(false);
  
  const navigate = useNavigate();
  const { user } = useAuth();
  const { confirm } = useConfirmation();
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const loadOmiseScript = () =>
    new Promise<void>((resolve, reject) => {
      if ((window as any).OmiseCard) {
        resolve();
        return;
      }
      const existing = document.querySelector('script[src="https://cdn.omise.co/omise.js"]');
      if (existing) {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', () => reject(new Error('Failed to load Omise script')));
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://cdn.omise.co/omise.js';
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Omise script'));
      document.body.appendChild(script);
    });

 

  // Debounced cart refresh function
  const refreshCart = async (force = false) => {
    // If already refreshing, don't start another refresh
    if (refreshTimeoutRef.current && !force) {
      return;
    }
    
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }
    
    refreshTimeoutRef.current = setTimeout(async () => {
      try {
        const cartData = await apiService.cart.getCart(force);
        setCart(cartData.cart);
      } catch (error) {
        console.error('Error refreshing cart:', error);
      } finally {
        refreshTimeoutRef.current = null;
      }
    }, force ? 0 : 500); // Immediate refresh if forced, otherwise debounce
  };

  // Get payment method icon
  const getPaymentIcon = (paymentMethod: string) => {
    switch (paymentMethod.toLowerCase()) {
      case 'bkash':
        return 'mobile-alt';
      case 'nagad':
        return 'mobile-alt';
      case 'rocket':
        return 'mobile-alt';
      case 'sslcommerz':
      case 'sslc':
        return 'credit-card';
      case 'omise':
        return 'credit-card';
      case 'pathao-pay':
      case 'pathao_pay':
        return 'truck';
      case 'free':
        return 'gift';
    }
  };

  // Get payment method display name
  const getPaymentDisplayName = (paymentMethod: string) => {
    switch (paymentMethod.toLowerCase()) {
      case 'bkash':
        return 'bKash';
      case 'sslcommerz':
      case 'sslc':
        return 'Card & Others';
      case 'omise':
        return 'Omise';
      case 'pathao-pay':
      case 'pathao_pay':
        return 'Pathao Pay';
      case 'free':
        return 'Free';
      default:
        return paymentMethod;
    }
  };

  // Note: Payment callbacks are now handled by PaymentCallbackPage component

  // Load cart data
  useEffect(() => {
    const loadCart = async () => {
      try {
        setLoading(true);
        setError(null);
        setErrorMessages(null);
        const cartData = await apiService.cart.getCart();
        setCart(cartData.cart);
        
        // Load payment methods
        if (!cartData.cart.empty) {
          try {
            const paymentData = await apiService.cart.getPaymentMethods();
            if (paymentData.payment_methods.length === 1) {
              setSelectedPaymentMethod(paymentData.payment_methods[0].value);
            }
            setIsPartialPayment(paymentData.has_partial_payment_option);
          } catch (error) {
            console.error('Error loading payment methods:', error);
          }
        }
      } catch (error) {
        console.error('Error loading cart:', error);
        setError('Failed to load cart. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadCart();

    // Listen for cart update events
    const handleCartUpdate = () => {
      loadCart();
    };

    window.addEventListener('cartUpdated', handleCartUpdate);
    
    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdate);
    };
  }, []);

  // Note: Payment callbacks are now handled by PaymentCallbackPage component

  // Load shipping addresses if user is authenticated
  useEffect(() => {
    if (user && cart?.is_shipping_address_required && cart?.id) {
      const loadAddresses = async () => {
        try {
          setLoadingAddresses(true);
          const addressData = await apiService.cart.getShippingAddresses();
          setShippingAddresses(addressData.addresses);
          
          // Set default address if available
          const defaultAddress = addressData.addresses.find(addr => addr.is_default);
          if (defaultAddress) {
            setSelectedAddress(defaultAddress.id);
          }
        } catch (error) {
          console.error('Error loading shipping addresses:', error);
        } finally {
          setLoadingAddresses(false);
        }
      };
      
      loadAddresses();
    }
  }, [user, cart?.id, cart?.is_shipping_address_required]);

  // Cart timer effect - only run once when component mounts or cart changes significantly
  const hasExpiredRef = useRef(false);
  
  useEffect(() => {
    // Clear any existing timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    
    // Reset expired flag when cart changes
    hasExpiredRef.current = false;

    if (cart?.expires_at) {
      const updateTimer = () => {
        const now = new Date().getTime();
        const expiresAt = new Date(cart.expires_at!).getTime();
        const timeLeft = expiresAt - now;
        
        if (timeLeft > 0) {
          const minutes = Math.floor(timeLeft / (1000 * 60));
          const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);
          setCartTimer(`${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
        } else {
          setCartTimer('00:00');
          // Cart expired, reload cart data only once
          if (!hasExpiredRef.current) {
            hasExpiredRef.current = true;
            refreshCart(true);
          }
        }
      };
      
      updateTimer();
      timerRef.current = setInterval(updateTimer, 1000);
    }
    
    // Cleanup function
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [cart?.id, cart?.expires_at]); // Depend on both ID and expires_at

  // Remove item from cart
  const handleRemoveItem = async (itemId: number) => {
    const confirmed = await confirm({
      title: 'Remove Item',
      message: 'Are you sure you want to remove this item from your cart?',
      confirmText: 'Remove',
      cancelText: 'Cancel',
      variant: 'danger',
      icon: 'fa-trash'
    });
    
    if (!confirmed) {
      return;
    }
    
    try {
      setRemovingItem(itemId);
      const result = await apiService.cart.removeFromCart(itemId);
      // Update cart from API response instead of making another request
      if (result.cart) {
        setCart(result.cart);
      } else {
        // Fallback to refresh if response doesn't include cart
        refreshCart(true);
      }
      setToastMessage('Item removed from cart');
      setShowToast(true);
      
      // Dispatch cart update event for header
      window.dispatchEvent(new CustomEvent('cartUpdated', { detail: { cart: result.cart } }));
    } catch (error) {
      console.error('Error removing item from cart:', error);
      setError('Failed to remove item from cart. Please try again.');
    } finally {
      setRemovingItem(null);
    }
  };

  // Add addon to cart
  const handleAddAddonToCart = async (addon: Addon, quantity: number = 1) => {
    try {
      if (addon.has_variants) {
        // Navigate to addon detail page for variant selection
        navigate(addon.get_add_to_cart_url);
      } else {
        const result = await apiService.cart.addToCart('addon', addon.id, quantity);
        // Update cart from API response instead of making another request
        if (result.cart) {
          setCart(result.cart);
        } else {
          // Fallback to refresh if response doesn't include cart
          refreshCart(true);
        }
        setToastMessage(`${addon.name} added to cart`);
        setShowToast(true);
        
        // Dispatch cart update event for header
        window.dispatchEvent(new CustomEvent('cartUpdated', { detail: { cart: result.cart } }));
      }
    } catch (error: any) {
      console.error('Error adding addon to cart:', error);
      const errorMessage = error?.response?.data?.error || 'Failed to add addon to cart. Please try again.';
      setError(errorMessage);
      setToastMessage(errorMessage);
      setShowToast(true);
    }
  };


  // Clear entire cart
  const handleClearCart = async () => {
    const confirmed = await confirm({
      title: 'Clear Cart',
      message: 'Are you sure you want to clear your cart? This action cannot be undone.',
      confirmText: 'Clear Cart',
      cancelText: 'Cancel',
      variant: 'danger',
      icon: 'fa-trash-alt'
    });
    
    if (!confirmed) {
      return;
    }

    try {
      await apiService.cart.clearCart();
      setCart(null);
    } catch (error) {
      console.error('Error clearing cart:', error);
      setError('Failed to clear cart. Please try again.');
    }
  };

  // Apply coupon
  const handleApplyCoupon = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!couponCode.trim()) return;

    try {
      setApplyingCoupon(true);
      const result = await apiService.cart.applyCoupon(couponCode.trim());
      
      if (result.success) {
        setCart(result.cart);
        setCouponCode('');
        setToastMessage(result.message);
        setShowToast(true);
      } else {
        setError(result.message);
      }
    } catch (error) {
      console.error('Error applying coupon:', error);
      setError('Failed to apply coupon. Please try again.');
    } finally {
      setApplyingCoupon(false);
    }
  };

  // Remove coupon
  const handleRemoveCoupon = async () => {
    try {
      const result = await apiService.cart.removeCoupon();
      setCart(result.cart);
      setToastMessage(result.message);
      setShowToast(true);
    } catch (error) {
      console.error('Error removing coupon:', error);
      setError('Failed to remove coupon. Please try again.');
    }
  };

  // Extend cart time
  const handleExtendCartTime = async () => {
    try {
      const result = await apiService.cart.extendCartTime();
      if (result.success) {
        setCart(result.cart);
        setToastMessage('Cart time extended successfully!');
        setShowToast(true);
      }
    } catch (error) {
      console.error('Error extending cart time:', error);
      setError('Failed to extend cart time. Please try again.');
    }
  };

  // Set shipping address
  const handleSetShippingAddress = async (addressId: number) => {
    try {
      const result = await apiService.cart.setShippingAddress(addressId);
      setCart(result.cart);
      setSelectedAddress(addressId);
      setToastMessage('Shipping address set successfully!');
      setShowToast(true);
    } catch (error) {
      console.error('Error setting shipping address:', error);
      setError('Failed to set shipping address. Please try again.');
    }
  };

  // Proceed to checkout
  const handleCheckout = async () => {
    if (!selectedPaymentMethod) {
      setError('Please select a payment method.');
      return;
    }

    if (cart?.is_shipping_address_required && !selectedAddress) {
      setError('Please select a shipping address.');
      return;
    }

    try {
      setCheckingOut(true);
      setError(null);
      setErrorMessages(null);

      const checkoutData = {
        payment_choice: selectedPaymentMethod,
        is_partial_payment: isPartialPayment,
        create_account: createAccount && !user
      };

      const result = await apiService.cart.proceedToCheckout(checkoutData);
      
      if (result.success) {
        // Handle different types of redirects
        if (result.redirect_url) {
          // Check if it's a payment gateway URL or confirmation URL
          if (result.redirect_url.includes('/payment/')) {
            // This is a payment gateway URL, redirect directly
            window.location.href = result.redirect_url;
          } else if (result.redirect_url.includes('/confirmation/')) {
            // This is a confirmation URL (free payment), redirect directly
            window.location.href = result.redirect_url;
          } else {
            // Fallback: try to determine the payment gateway and initialize payment
            const gateway = selectedPaymentMethod.toLowerCase();
            await handlePaymentInitialization(gateway);
          }
        } else {
          // No redirect URL provided (API flow) -> initialize payment via API
          const gateway = selectedPaymentMethod.toLowerCase();
          await handlePaymentInitialization(gateway);
        }
      } else {
        setError(result.message);
      }
    } catch (err: any) {
      console.error('Error during checkout:', err);
      const data = err.response?.data;
      const messages = Array.isArray(data?.messages) ? data.messages : null;
      setError(data?.error || 'Failed to proceed to checkout. Please try again.');
      setErrorMessages(messages);
    } finally {
      setCheckingOut(false);
    }
  };

  // Omise: poll charge status when QR is shown, then validate and redirect
  useEffect(() => {
    if (!omisePayment || !cart?.id) return;
    const chargeId = omisePayment.chargeId;
    const interval = setInterval(async () => {
      try {
        const statusResult = await apiService.payment.getOmiseStatus(cart.id, chargeId);
        if (statusResult.status) {
          setOmiseStatus(statusResult.status as any);
        }
        setOmiseStatusMessage(statusResult.message || 'Checking...');
        if (statusResult.status === 'successful') {
          clearInterval(interval);
          const validateResult = await apiService.payment.validateOmise(cart.id, { charge_id: chargeId });
          if (validateResult.success && validateResult.redirect_url) {
            setOmisePayment(null);
            window.location.href = validateResult.redirect_url;
          }
        } else if (statusResult.status === 'expired' || statusResult.status === 'failed') {
          clearInterval(interval);
          setOmiseStatusMessage(statusResult.message || 'Payment expired or failed.');
        }
      } catch {
        // Keep polling on transient errors
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [omisePayment, cart?.id]);

  const handleOmiseSourceSelect = async (sourceType: string) => {
    if (!cart?.id || !omiseConfig) {
      setError('Omise configuration not available.');
      return;
    }
    setShowOmiseSelector(false);
    setCheckingOut(true);
    setError(null);
    setErrorMessages(null);
    setOmiseStatus('pending');

    try {
      if (sourceType === 'credit_card') {
        await loadOmiseScript();
        const OmiseCard = (window as any).OmiseCard;
        if (!OmiseCard) {
          setError('Omise payment form is not available.');
          return;
        }
        OmiseCard.configure({
          publicKey: omiseConfig.publicKey,
          defaultPaymentMethod: 'credit_card',
        });
        OmiseCard.open({
          amount: omiseConfig.amountSubunit,
          currency: omiseConfig.currency,
          defaultPaymentMethod: 'credit_card',
          onCreateTokenSuccess: async (token: string) => {
            const result = await apiService.payment.createOmisePayment(cart.id, {
              source_type: 'credit_card',
              omiseToken: token,
            });
            if (result.success) {
            if (result.redirect_url) {
              window.location.href = result.redirect_url;
            } else if (result.qr_code_data && result.charge_id) {
              setOmisePayment({ qrCodeData: result.qr_code_data, chargeId: result.charge_id });
              setOmiseStatusMessage(result.message || 'Scan QR code to complete payment');
              } else {
                setError(result.message || 'No payment URL received.');
              }
            } else {
              setError(result.error || result.message || 'Failed to create card payment.');
            }
          },
          onError: (err: any) => {
            setError(err?.message || 'Card payment form error.');
          },
        });
        return;
      }

      const result = await apiService.payment.createOmisePayment(cart.id, { source_type: sourceType });
      if (result.success) {
        if (result.redirect_url) {
          window.location.href = result.redirect_url;
        } else if (result.qr_code_data && result.charge_id) {
          setOmisePayment({ qrCodeData: result.qr_code_data, chargeId: result.charge_id });
          setOmiseStatusMessage(result.message || 'Scan QR code to complete payment');
        } else {
          setError(result.message || 'No payment URL or QR received.');
        }
      } else {
        setError(result.error || result.message || 'Failed to initialize Omise payment.');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to initialize Omise payment.');
    } finally {
      setCheckingOut(false);
    }
  };

  // Handle payment initialization for specific gateways
  const handlePaymentInitialization = async (gateway: string) => {
    if (!cart?.id) {
      setError('Cart ID not found.');
      return;
    }

    try {
      setCheckingOut(true);
      setError(null);
      setErrorMessages(null);

      // Omise: show payment method selector, then create payment
      if (gateway === 'omise') {
        const config = await apiService.payment.getOmiseConfig(cart.id);
        if (config.success) {
          setOmiseConfig({
            sourceTypes: config.source_types,
            publicKey: config.omise_public_key,
            amountSubunit: config.amount_subunit,
            currency: config.currency,
          });
          setShowOmiseSelector(true);
        } else {
          setError(config.error || 'Failed to load Omise payment methods.');
        }
        setCheckingOut(false);
        return;
      }

      const result = await apiService.payment.initializePayment(cart.id, gateway);
      
      if (result.success) {
        if (result.redirect_url) {
          window.location.href = result.redirect_url;
        } else if (result.form_html) {
          const formContainer = document.createElement('div');
          formContainer.innerHTML = result.form_html;
          const form = formContainer.querySelector('form');
          if (form) {
            document.body.appendChild(form);
            form.submit();
          } else {
            setError('Payment form not found.');
          }
        } else {
          setError('No payment method available for this gateway.');
        }
      } else {
        setError(result.message || 'Failed to initialize payment.');
      }
    } catch (err: any) {
      console.error('Error initializing payment:', err);
      setError(err.response?.data?.error || 'Failed to initialize payment. Please try again.');
    } finally {
      setCheckingOut(false);
    }
  };

  if (loading) {
    return (
      <div className="cart-page">
        <Container className="py-4">
          <Row className="g-4">
            <Col xl={8}>
              <SkeletonLoader type="text" width="150px" height="32px" className="mb-4" />
              <CartItemSkeleton />
              <CartItemSkeleton />
              <CartItemSkeleton />
            </Col>
            <Col xl={4}>
              <div className="cart-summary-skeleton">
                <SkeletonLoader type="text" width="200px" height="28px" className="mb-3" />
                <SkeletonLoader type="text" width="100%" height="20px" className="mb-2" />
                <SkeletonLoader type="text" width="100%" height="20px" className="mb-2" />
                <SkeletonLoader type="text" width="80%" height="20px" className="mb-4" />
                <SkeletonLoader type="rect" width="100%" height="48px" />
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <CartError
        error={error}
        messages={errorMessages}
        onRetry={() => window.location.reload()}
      />
    );
  }

  if (!cart || cart.empty) {
    return (
      <EmptyCart onBrowseEvents={() => window.location.href = "/events"} />
    );
  }

  return (
    <div className="cart-page">
      <SEO 
        title="Shopping Cart | Quicket"
        description="Review your cart and proceed to checkout on Quicket."
      />

      {/* Omise drawers */}
      <OmiseDrawer
        showSelector={showOmiseSelector}
        showQr={!!omisePayment}
        omiseConfig={omiseConfig}
        qrCodeData={omisePayment?.qrCodeData}
        status={omiseStatus}
        statusMessage={omiseStatusMessage}
        onSelectSource={handleOmiseSourceSelect}
        onClose={() => {
          if (omisePayment) {
            setOmisePayment(null);
          } else {
            setShowOmiseSelector(false);
          }
        }}
      />
      <Container className="py-4">
        <Row className="g-4">
          <Col xl={8}>
            <div className="cart-header d-flex justify-content-between align-items-center mb-4 flex-wrap">
              <h2 className="mb-0">Your Cart</h2>
              <div className="d-flex align-items-center gap-3 flex-wrap">
                {paymentStatus && (
                  <div className={`payment-status-indicator ${paymentStatus}`}>
                    <i className={`fas fa-${paymentStatus === 'success' ? 'check-circle' : paymentStatus === 'failed' ? 'times-circle' : 'spinner'} me-2`}></i>
                    Payment {paymentStatus}
                  </div>
                )}
                {!cart.empty && (
                  <div className="cart-timer-container">
                    <i className="fas fa-clock me-2"></i>
                    <span className="cart-timer">{cartTimer}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="cart-items">
              {cart.items.map((item, index) => {
                // Use get_image_url from API if available, fallback to manual detection
                const itemImage = item.get_image_url || 
                                 item.event_pass?.event?.banner_url || 
                                 item.event_pass?.event?.thumbnail_url ||
                                 item.addon?.image_url ||
                                 item.addon?.image ||
                                 item.addon_order?.addon?.image_url ||
                                 item.addon_order?.addon?.image ||
                                 item.activity_booking?.activity_place?.thumbnail_url;
                
                // Use get_item_display for title, get_item_details for subtitle
                const itemTitle = item.get_item_display || item.get_item_details;
                const itemSubtitle = item.get_item_display ? item.get_item_details : null;
                
                // Get unit price (use price field from API if available)
                const unitPrice = item.price || (item.total / item.quantity);
                
                // Check for seat details in information
                const seatDetails = item.information?.seat_details;
                
                return (
                  <div key={`${item.id}-${index}`} className="glass rounded-3 mb-3 cart-item-wrapper">
                    <div className="cart-item-content p-4">
                      <div className="d-flex align-items-center">
                        <div className="me-3 cart-item-image-wrapper">
                          {itemImage && itemImage !== '#' ? (
                            <img 
                              src={itemImage} 
                              alt={itemTitle}
                              className="rounded-3"
                              style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                              loading="lazy"
                            />
                          ) : (
                            <div 
                              className="rounded-3 d-flex align-items-center justify-content-center"
                              style={{ 
                                width: '100px', 
                                height: '100px', 
                                background: 'linear-gradient(135deg, rgba(var(--bs-primary-rgb), 0.1) 0%, rgba(var(--bs-secondary-rgb), 0.1) 100%)',
                                color: 'var(--bs-primary)',
                                fontSize: '2rem'
                              }}
                            >
                              <i className="fas fa-ticket-alt"></i>
                            </div>
                          )}
                        </div>
                        <div className="flex-grow-1 overflow-hidden cart-item-info">
                          <h5 className="text-truncate text-primary mb-2 text-left">{itemTitle}</h5>
                          {itemSubtitle && (
                            <p className="text-muted mb-0 text-start">{itemSubtitle}</p>
                          )}
                          {seatDetails && Array.isArray(seatDetails) && seatDetails.length > 0 && (
                            <div className="mt-2">
                              <small className="text-muted d-block mb-1">Selected Seats:</small>
                              <div className="d-flex flex-wrap gap-1">
                                {seatDetails.map((seat: any, seatIndex: number) => (
                                  <Badge key={seatIndex} bg="primary">{seat.seat_number}</Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="flex-shrink-0 ms-3">
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleRemoveItem(item.id)}
                            disabled={removingItem === item.id}
                            title="Remove item"
                          >
                            {removingItem === item.id ? (
                              <Spinner size="sm" animation="border" />
                            ) : (
                              <i className="fas fa-trash"></i>
                            )}
                          </Button>
                        </div>
                      </div>
                      <div className="row mt-3 cart-item-pricing">
                        <div className="col-4">
                          <small className="text-muted d-block mb-1">Price</small>
                          <span className="fw-bold">
                            {unitPrice === 0 ? 'Free' : `${cart.currency?.symbol || '$'}${unitPrice.toFixed(1)}`}
                          </span>
                        </div>
                        <div className="col-4">
                          <small className="text-muted d-block mb-1">Quantity</small>
                          <span className="fw-bold">{item.quantity}</span>
                        </div>
                        <div className="col-4">
                          <small className="text-muted d-block mb-1">Total</small>
                          <span className={`fw-bold ${item.total === 0 ? 'text-success' : ''}`}>
                            {item.total === 0 ? 'Free' : `${cart.currency?.symbol || '$'}${item.total.toFixed(0)}`}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Addons Section */}
            {cart.addons && cart.addons.length > 0 && (
              <div className="addons-section mt-4">
                <div className="addons-section-header">
                  <h4 className="addons-section-title">
                    <i className="fas fa-gift"></i>
                    <span>Available Add-ons</span>
                  </h4>
                </div>
                
                {/* Group addons by cluster */}
                {(() => {
                  const clusterMap = new Map<number, Addon[]>();
                  const noClusterAddons: Addon[] = [];
                  
                  cart.addons.forEach((addon) => {
                    if (addon.cluster) {
                      const clusterId = addon.cluster.id;
                      if (!clusterMap.has(clusterId)) {
                        clusterMap.set(clusterId, []);
                      }
                      clusterMap.get(clusterId)!.push(addon);
                    } else {
                      noClusterAddons.push(addon);
                    }
                  });
                  
                  return (
                    <div className="addons-content">
                      {/* Addons with clusters */}
                      {Array.from(clusterMap.entries()).map(([clusterId, addons]) => {
                        const cluster = addons[0].cluster!;
                        return (
                          <div key={clusterId} className="addon-cluster">
                            <div className="cluster-header">
                              <div className="cluster-header-content">
                                {cluster.icon && <i className={`${cluster.icon} cluster-icon`}></i>}
                                <div>
                                  <h5 className="cluster-name">{cluster.name}</h5>
                                  {cluster.description && (
                                    <p className="cluster-description">{cluster.description}</p>
                                  )}
                                </div>
                              </div>
                              <button
                                type="button"
                                className="cluster-toggle"
                                onClick={(e) => {
                                  const target = document.getElementById(`cluster-${clusterId}`);
                                  if (target) {
                                    const isExpanded = !target.classList.contains('collapse');
                                    if (isExpanded) {
                                      target.classList.add('collapse');
                                    } else {
                                      target.classList.remove('collapse');
                                      target.classList.add('show');
                                    }
                                  }
                                }}
                                aria-label="Toggle cluster"
                              >
                                <i className="fas fa-chevron-down"></i>
                              </button>
                            </div>
                            <div className="collapse show" id={`cluster-${clusterId}`}>
                              <div className="addons-grid">
                                {addons.map((addon) => (
                                  <AddonCard key={addon.id} addon={addon} cart={cart} onAddToCart={handleAddAddonToCart} />
                                ))}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      
                      {/* Addons without cluster */}
                      {noClusterAddons.length > 0 && (
                        <div className="addons-grid">
                          {noClusterAddons.map((addon) => (
                            <AddonCard key={addon.id} addon={addon} cart={cart} onAddToCart={handleAddAddonToCart} />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
          </Col>

          <Col xl={4}>
            <Card className="border-0 glass">
              <Card.Body className="p-4">
                <h5 className="mb-4 d-flex justify-content-between align-items-center">
                  <span className="float-start">Order Summary</span>
                  <span className="text-muted small">{cart.id ? `#${cart.id}` : ""}</span>
                </h5>
                
                <div className="table-responsive">
                  <table className="table table-borderless mb-0">
                    <tbody>
                      <tr>
                        <td className="text-muted text-start">Subtotal</td>
                        <td className="text-end">{cart.currency?.symbol || '$'}{(cart.subtotal ?? cart.total).toFixed(2)}</td>
                      </tr>
                      {cart.partial_payment && (
                        <tr>
                          <td className="text-muted text-start">Dues</td>
                          <td className="text-end text-danger">
                            - {cart.currency?.symbol || '$'}{(cart.partial_amount ?? 0).toFixed(2)}
                          </td>
                        </tr>
                      )}
                      {(cart.vat_tax ?? 0) > 0 && (
                        <tr>
                          <td className="text-muted text-start">VAT</td>
                          <td className="text-end">{cart.currency?.symbol || '$'}{(cart.vat_tax ?? 0).toFixed(2)}</td>
                        </tr>
                      )}
                      <tr>
                        <td className="text-muted text-start">Platform Fee</td>
                        <td className="text-end">{cart.currency?.symbol || '$'}{(cart.platform_fee ?? 0).toFixed(2)}</td>
                      </tr>
                      {(cart.shipping_charge ?? 0) > 0 && (
                        <tr>
                          <td className="text-muted text-start">Shipping</td>
                          <td className="text-end">{cart.currency?.symbol || '$'}{cart.shipping_charge.toFixed(2)}</td>
                        </tr>
                      )}
                      {cart.coupon_applicable && (
                        <tr>
                          {cart.coupon ? (
                            <>
                              <td>
                                <div className="d-flex align-items-center">
                                  <span className="me-2">Coupon ({cart.coupon.code})</span>
                                  <Button 
                                    variant="outline-danger" 
                                    size="sm"
                                    onClick={handleRemoveCoupon}
                                  >
                                    <i className="fas fa-times"></i>
                                  </Button>
                                </div>
                              </td>
                              <td className="text-end text-success">
                                - {cart.currency?.symbol || '$'}{(cart.coupon.discount ?? 0).toFixed(2)}
                              </td>
                            </>
                          ) : (
                            <td colSpan={2}>
                              <Form onSubmit={handleApplyCoupon} className="d-flex">
                                <Form.Control
                                  type="text"
                                  placeholder="Enter coupon code"
                                  value={couponCode}
                                  onChange={(e) => setCouponCode(e.target.value)}
                                  className="me-2"
                                />
                                <Button 
                                  type="submit" 
                                  variant="primary"
                                  disabled={applyingCoupon}
                                >
                                  {applyingCoupon ? <Spinner size="sm" /> : 'Apply'}
                                </Button>
                              </Form>
                            </td>
                          )}
                        </tr>
                      )}
                      <tr className="border-top">
                        <td className="fw-bold pt-3 text-start">Total</td>
                        <td className="text-end pt-3">
                          <span className="fw-bold fs-5 text-primary">
                            {cart.total === 0 ? 'Free' : `${cart.currency?.symbol || '$'}${(cart.total ?? 0).toFixed(2)}`}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {cart.shipping_address && (
                  <div className="mt-4">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="mb-0">Shipping Address</h6>
                    </div>
                    <p className="text-muted mb-0">{cart.shipping_address.string_address}</p>
                  </div>
                )}

                {cart.is_shipping_address_required && !cart.shipping_address && (
                  <div className="mt-4">
                    <h6 className="mb-3">Select Shipping Address</h6>
                    {loadingAddresses ? (
                      <Spinner size="sm" />
                    ) : shippingAddresses.length > 0 ? (
                      <div className="d-grid gap-2">
                        {shippingAddresses.map((address) => (
                          <Button
                            key={address.id}
                            variant={selectedAddress === address.id ? "primary" : "outline-primary"}
                            size="sm"
                            onClick={() => handleSetShippingAddress(address.id)}
                          >
                            {address.string_address}
                          </Button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted">No addresses found. Please add an address first.</p>
                    )}
                  </div>
                )}

                {!cart.empty && (
                  <Form onSubmit={(e) => { e.preventDefault(); handleCheckout(); }} className="mt-4">
                    {cart.has_partial_payment_option && (
                      <div className="mb-3">
                        <Button
                          type="button"
                          variant={isPartialPayment ? "primary" : "outline-primary"}
                          className="w-100"
                          onClick={() => setIsPartialPayment(!isPartialPayment)}
                        >
                          Pay Partial: {cart.currency?.symbol || '$'}{cart.partial_payment_amount.toFixed(2)}
                        </Button>
                      </div>
                    )}

                    <div className="mb-3" id="payment-method-section">
                      <Form.Label className="payment-method-label mb-2">
                        <i className="fas fa-credit-card me-2"></i>
                        Payment Method
                      </Form.Label>
                      <div className="payment-methods-list">
                        {cart.payment_choices.map((choice) => {
                          const displayName = getPaymentDisplayName(choice.value);
                          const icon = getPaymentIcon(choice.value);
                          const isSelected = selectedPaymentMethod === choice.value;
                          
                          return (
                            <button
                              key={choice.value}
                              type="button"
                              className={`payment-method-item ${isSelected ? 'selected' : ''} ${choice.disabled ? 'disabled' : ''}`}
                              onClick={() => !choice.disabled && setSelectedPaymentMethod(choice.value)}
                              disabled={choice.disabled}
                            >
                              <div className="payment-method-icon-wrapper">
                                <i className={`fas fa-${icon}`}></i>
                              </div>
                              <span className="payment-method-name">{displayName}</span>
                              {isSelected && (
                                <i className="fas fa-check-circle payment-method-check-icon"></i>
                              )}
                              {choice.disabled && (
                                <small className="payment-method-unavailable">Unavailable</small>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {!user && (
                      <Form.Check
                        type="checkbox"
                        label="Create an account for me"
                        checked={createAccount}
                        onChange={(e) => setCreateAccount(e.target.checked)}
                        className="mb-3"
                      />
                    )}

                    <p className="text-muted small mb-3">
                      By proceeding to checkout, I confirm that I have read and agree to the 
                      <Link to="/terms" target="_blank" className="text-decoration-none"> Terms & Conditions</Link> 
                      and all applicable policies.
                    </p>

                    <Button 
                      type="submit" 
                      variant="secondary" 
                      size="lg" 
                      className="w-100"
                      disabled={!selectedPaymentMethod || checkingOut || paymentStatus === 'processing'}
                    >
                      {checkingOut || paymentStatus === 'processing' ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          {paymentStatus === 'processing' ? 'Processing Payment...' : 'Processing...'}
                        </>
                      ) : (
                        <>
                          <i className="fas fa-lock me-2"></i>
                          Secure Checkout
                        </>
                      )}
                    </Button>
                  </Form>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      <ToastContainer position="bottom-end" className="p-3">
        <Toast show={showToast} onClose={() => setShowToast(false)} delay={3000} autohide>
          <Toast.Header>
            <i className="fas fa-check-circle text-success me-2"></i>
            <strong className="me-auto">Success</strong>
          </Toast.Header>
          <Toast.Body>{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

export default CartPage;
